import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from '../../../i18n';
import { getContentTemplateDetail } from '../../../store/request';
import type { TemplateDetail } from '../../../types/template';
import { scriptChannelMeta, type ScriptChannel } from './scriptData';

type ScriptDetailModalProps = {
  open: boolean;
  templateId?: number;
  shopId?: string | null;
  onClose: () => void;
  onEdit?: (detail: TemplateDetail) => void;
};

function toScriptChannel(channel?: string): ScriptChannel | null {
  const value = channel?.trim().toUpperCase();
  if (value === 'EMAIL') return 'email';
  if (value === 'DM') return 'dm';
  return null;
}

function formatTime(value?: string) {
  if (!value) return '—';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format('YYYY-MM-DD HH:mm:ss');
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="w-20 shrink-0 pt-0.5 text-right text-xs font-medium text-slate-400">{label}</div>
      <div className="min-w-0 flex-1 text-sm text-slate-800">{children}</div>
    </div>
  );
}

export function ScriptDetailModal({ open, templateId, shopId, onClose, onEdit }: ScriptDetailModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const currentShopId = shopId?.trim() || '';

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setLoading(false);
      return;
    }
    if (!currentShopId || templateId == null) {
      setDetail(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    getContentTemplateDetail(templateId, { shopId: currentShopId })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || t('script.detail_load_failed'));
          setDetail(null);
          return;
        }
        setDetail(res.content);
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('script.detail_load_failed'));
          setDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, templateId, currentShopId, t]);

  const channel = toScriptChannel(detail?.channel);
  const title = detail?.name?.trim() || t('script.detail_title');

  return (
    <Modal
      title={loading ? t('script.detail_title') : title}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={560}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose}>
            {t('common.close')}
          </Button>
          {onEdit && detail ? (
            <Button
              type="primary"
              className="wb-btn-primary rounded-lg"
              onClick={() => onEdit(detail)}
            >
              {t('common.edit')}
            </Button>
          ) : null}
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-10">
          <Spin />
        </div>
      ) : !detail ? (
        <p className="mb-0 py-6 text-center text-sm text-slate-400">{t('script.detail_empty')}</p>
      ) : (
        <div className="space-y-4 pt-1">
          <DetailRow label={t('script.name')}>
            <span className="font-medium">{detail.name?.trim() || '—'}</span>
          </DetailRow>
          <DetailRow label={t('script.channel')}>
            {channel ? (
              <Tag className={`m-0 border-0 ${scriptChannelMeta[channel].className}`}>
                {t(`script.channel_${channel}`)}
              </Tag>
            ) : (
              detail.channel?.trim() || '—'
            )}
          </DetailRow>
          <DetailRow label={t('script.subject')}>
            <span className="whitespace-pre-wrap break-words">{detail.subject?.trim() || '—'}</span>
          </DetailRow>
          <DetailRow label={t('script.body')}>
            <div className="whitespace-pre-wrap break-words rounded-lg border border-[#eef0f4] bg-[#f8fafc] px-3 py-2.5 text-sm leading-relaxed text-slate-700">
              {detail.body?.trim() || '—'}
            </div>
          </DetailRow>
          <DetailRow label={t('script.col_created')}>
            <span className="text-slate-500">{formatTime(detail.ctime)}</span>
          </DetailRow>
          <DetailRow label={t('script.col_updated')}>
            <span className="text-slate-500">{formatTime(detail.mtime)}</span>
          </DetailRow>
        </div>
      )}
    </Modal>
  );
}
